import { useCallback, useEffect, useMemo, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { RateProviderUsageService } from "../services/rateProviderUsage.service";
import type { RateProviderUsageMonth, RateProviderUsageReport } from "../types/rateProviderUsage.types";

/** How many months back the trend covers, including the running one. */
export const USAGE_TREND_MONTHS = 12;

export interface MonthOption {
  /** `YYYY-MM`, the value the selector round-trips. */
  value: string;
  from: Date;
  to: Date;
}

/**
 * Calendar months in UTC, newest first. UTC and not the browser's zone on purpose: the backend buckets in UTC
 * because that is how the providers bill, and a local-zone month would cut the window hours off the boundary
 * and move calls into the neighbouring month.
 */
export function buildMonthOptions(reference: Date = new Date()): MonthOption[] {
  const year = reference.getUTCFullYear();
  const month = reference.getUTCMonth();
  return Array.from({ length: USAGE_TREND_MONTHS }, (_, index) => {
    const from = new Date(Date.UTC(year, month - index, 1, 0, 0, 0, 0));
    const to = new Date(Date.UTC(year, month - index + 1, 1, 0, 0, 0, 0) - 1);
    return {
      value: `${from.getUTCFullYear()}-${String(from.getUTCMonth() + 1).padStart(2, "0")}`,
      from,
      to,
    };
  });
}

export interface UseRateProviderUsageResult {
  /** Counters for the selected month: the ladder, the errors and the daily series. */
  month: RateProviderUsageReport | null;
  /** Rolled up from the trend window's daily series, oldest first. */
  trend: RateProviderUsageMonth[];
  /** Every provider seen in either window, in failover order first, then the retired ones. */
  providerCodes: string[];
  isLoading: boolean;
  isRefreshing: boolean;
  hasError: boolean;
  /** A 401/403 latches for the session: the whole surface becomes a no-access view. */
  hasAccess: boolean;
  refresh: () => void;
}

function rollUpByMonth(report: RateProviderUsageReport | null): RateProviderUsageMonth[] {
  if (!report) return [];
  const buckets = new Map<string, RateProviderUsageMonth>();
  for (const provider of report.providers) {
    for (const day of provider.by_day) {
      // `day` is already a UTC-bucketed `YYYY-MM-DD`, so the month is a prefix — parsing it back into a Date
      // would reintroduce the local-zone shift the backend went out of its way to avoid.
      const month = day.day.slice(0, 7);
      let bucket = buckets.get(month);
      if (!bucket) {
        bucket = { month, totalsByProvider: {}, total: 0 };
        buckets.set(month, bucket);
      }
      const totals = (bucket.totalsByProvider[provider.provider_code] ??= {
        total: 0,
        success: 0,
        failed: 0,
      });
      totals.total += day.total;
      totals.success += day.success;
      totals.failed += day.failed;
      bucket.total += day.total;
    }
  }
  return [...buckets.values()].sort((a, b) => a.month.localeCompare(b.month));
}

/**
 * State of the rate-provider quota page: one report for the selected month and one for the trend window.
 *
 * Two calls and not one because the headline counters, the error breakdown and the last error are aggregated
 * over the WHOLE window — asking a twelve-month window for them would answer "this year", not "this month",
 * which is the number that has to be compared against the monthly plan.
 *
 * Freshness: no polling. The counter only moves when a rate is refreshed, and staff open this page to read a
 * month, not to watch a live feed.
 */
export function useRateProviderUsage(selectedMonth: MonthOption): UseRateProviderUsageResult {
  const [month, setMonth] = useState<RateProviderUsageReport | null>(null);
  const [trendReport, setTrendReport] = useState<RateProviderUsageReport | null>(null);
  const [hasError, setHasError] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // Loading is DERIVED (an attempt was requested but has not settled) — no synchronous setState in the
  // effect. Changing the month brings the skeletons back: the previous month's counters must not sit under
  // the new month's label, which is exactly the misreading this page exists to prevent.
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const attemptKey = `${reloadKey}:${selectedMonth.value}`;
  const isLoading = settledKey !== attemptKey && !isRefreshing;

  const trendWindow = useMemo(() => {
    const oldest = buildMonthOptions()[USAGE_TREND_MONTHS - 1];
    return { from: oldest.from, to: new Date() };
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      RateProviderUsageService.get(selectedMonth.from, selectedMonth.to),
      RateProviderUsageService.get(trendWindow.from, trendWindow.to),
    ])
      .then(([monthReport, trend]) => {
        if (cancelled) return;
        setMonth(monthReport);
        setTrendReport(trend);
        setHasError(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (HttpApiService.isAccessDeniedError(error)) setHasAccess(false);
        else setHasError(true);
      })
      .finally(() => {
        if (cancelled) return;
        setSettledKey(attemptKey);
        setIsRefreshing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [attemptKey, selectedMonth.from, selectedMonth.to, trendWindow.from, trendWindow.to]);

  const refresh = useCallback(() => {
    setIsRefreshing(true);
    setReloadKey((key) => key + 1);
  }, []);

  const trend = useMemo(() => rollUpByMonth(trendReport), [trendReport]);

  // Failover order first so the table's columns read like the ladder above it, then any provider that spent
  // quota in the window but is no longer in the priority list.
  const providerCodes = useMemo(() => {
    const ordered = month?.configured_priority ?? trendReport?.configured_priority ?? [];
    const seen = new Set(ordered);
    const extras = new Set<string>();
    for (const report of [month, trendReport]) {
      for (const provider of report?.providers ?? []) {
        if (!seen.has(provider.provider_code)) extras.add(provider.provider_code);
      }
    }
    return [...ordered, ...[...extras].sort()];
  }, [month, trendReport]);

  return { month, trend, providerCodes, isLoading, isRefreshing, hasError, hasAccess, refresh };
}
